import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styles from "./Style.module.css";
import SearchBar from "../../Search/SearchBar";
import DropDownIcon from "../../Icons/DropDownIcon";
import useClickOutside from "../../../Utils/Hooks/useClickOutside";
import useKeyboardNavigation from "../../../Utils/Hooks/useKeyboardNavigation";
import useCustomerSearch from "../../../Utils/Hooks/useCustomerSearch";
import {
	buildMembershipCustomerListExtraParams,
	isMembershipExpired,
	mapCustomerToMembershipClient,
} from "../../../Utils/customerSearchUtils";
import CustomerCreditDetails from "../../CustomerCreditDetails/CustomerCreditDetails";
import PrimiumMemberIcon from "../../Icons/PrimiumMemberIcon";
import TrashIcon from "../../Icons/TrashIcon";

const initialsFromName = (name) => {
	const s = String(name ?? "").trim();
	if (!s) return "?";
	const parts = s.split(/\s+/).filter(Boolean);
	if (parts.length === 0) return "?";
	if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
	return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// Only members can be selected here — keep this filter aligned with how
// `Clients.js` identifies active memberships from the customer-list response.
const isMemberCustomer = (customer) => {
	if (!customer) return false;
	const status = String(customer.membership_status ?? "").toLowerCase();
	if (status === "active") return true;
	if (Number(customer.membership) === 1) return true;
	return Boolean(customer.membership_number);
};

const MembershipClientDropdown = ({
	placeholder = "Search member by mobile/name/membership number",
	selectedClient = null,
	onSelect = null,
	onDropdownToggle = null,
	amountToCharge = 0,
	onTopUpClick = null,
	hasOpenMembershipSale = false,
	onOpenMembershipSale = null,
	openMembershipSaleLabel = "Go to Open Sale",
	user = null,
}) => {
	const [isDropdownOpen, setDropdownOpen] = useState(false);

	const dropdownRef = useRef(null);
	const dropdownMenuRef = useRef(null);
	const searchInputRef = useRef(null);

	const listExtraParams = useMemo(
		() => buildMembershipCustomerListExtraParams(user),
		[user]
	);

	const {
		filteredCustomers,
		searchQuery,
		setSearchQuery,
		isLoading,
		error: apiError,
		hasMore,
		fetchList,
		loadMore,
		reset,
		currentSearchRef,
	} = useCustomerSearch({
		extraParams: listExtraParams,
		enabled: isDropdownOpen,
		filterFn: isMemberCustomer,
	});

	const closeDropdown = useCallback(() => {
		setDropdownOpen(false);
		onDropdownToggle?.(false);
		reset();
	}, [onDropdownToggle, reset]);

	useClickOutside(dropdownRef, closeDropdown);

	// Initial load on open (re-fetch when booking user id becomes available).
	useEffect(() => {
		if (!isDropdownOpen) return;
		currentSearchRef.current = "";
		fetchList("");
	}, [isDropdownOpen, user, fetchList, currentSearchRef]);

	useEffect(() => {
		if (isDropdownOpen && dropdownMenuRef.current) {
			dropdownMenuRef.current.scrollIntoView({
				behavior: "smooth",
				block: "nearest",
			});
		}
	}, [isDropdownOpen]);

	useEffect(() => {
		if (isDropdownOpen && searchInputRef.current) {
			const t = setTimeout(() => {
				searchInputRef.current?.focus();
				searchInputRef.current?.select();
			}, 0);
			return () => clearTimeout(t);
		}
		return undefined;
	}, [isDropdownOpen]);

	const handleSelect = (customer) => {
		setDropdownOpen(false);
		onDropdownToggle?.(false);
		reset();
		if (typeof onSelect === "function") {
			onSelect(mapCustomerToMembershipClient(customer));
		}
	};

	const { selectedIndex, handleKeyDown: handleKeyboardNav } = useKeyboardNavigation(
		filteredCustomers.length,
		(item) => handleSelect(item),
		{ listRef: dropdownMenuRef }
	);

	const handleKeyDown = (e) => {
		handleKeyboardNav(e, (index) => filteredCustomers[index]);
	};

	const handleSearchChange = (e) => {
		setSearchQuery(e.target.value);
	};

	const toggleLabel = useMemo(() => {
		if (!selectedClient) return placeholder;
		const name = selectedClient.user_name || "—";
		const num = selectedClient.membership_number ?? "—";
		return `${name} (#${num})`;
	}, [placeholder, selectedClient]);

	const openingBalanceNum = Number(
		selectedClient?.credit_balance ?? selectedClient?.current_credit_balance ?? 0
	);
	const chargeNum = Number(amountToCharge);
	const safeCharge = Number.isFinite(chargeNum) ? chargeNum : 0;
	const closingBalanceNum = openingBalanceNum - safeCharge;
	const openingStr = Number.isFinite(openingBalanceNum) ? openingBalanceNum.toFixed(2) : "0.00";
	const closingStr = Number.isFinite(closingBalanceNum) ? closingBalanceNum.toFixed(2) : "0.00";

	const displayTitle = [selectedClient?.user_phone, selectedClient?.user_name]
		.filter(Boolean)
		.join(" ")
		.trim();
	const displayId =
		selectedClient?.membership_number ?? selectedClient?.id ?? selectedClient?.user_id ?? "—";

	const clearSelection = (e) => {
		e?.stopPropagation?.();
		if (typeof onSelect === "function") onSelect(null);
	};

	const openDropdown = () => {
		setDropdownOpen(true);
		onDropdownToggle?.(true);
	};

	const handleListScroll = (e) => {
		const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
		if (scrollHeight - scrollTop <= clientHeight + 5 && hasMore && !isLoading) {
			loadMore();
		}
	};

	return (
		<div
			ref={dropdownRef}
			className={`position-relative ${styles.membershipDropdownRoot}`}
			tabIndex={0}
		>
			{selectedClient ? (
				<div className={styles.selectedClientCard}>
					<div className={styles.selectedClientHeader}>
						<button
							type="button"
							className={styles.selectedClientHeaderMain}
							onClick={openDropdown}
						>
							<div className={styles.avatarWrap}>
								<div className={styles.avatarCircle} aria-hidden>
									{initialsFromName(selectedClient.user_name)}
								</div>
								<span className={styles.crownBadge} title="Member" aria-hidden>
									<PrimiumMemberIcon />
								</span>
							</div>
							<div className={styles.selectedClientText}>
								<div className={styles.selectedClientTitle}>{displayTitle || "—"}</div>
								<div className={styles.selectedClientId}>#{displayId}</div>
							</div>
						</button>
						<div className={styles.selectedClientActions}>
							<button
								type="button"
								className={`${styles.iconButton} icon`}
								onClick={clearSelection}
								aria-label="Remove member"
							>
								<TrashIcon />
							</button>
						</div>
					</div>

					<CustomerCreditDetails
						customerCreditDetails={[
							{
								current_credit_balance: openingStr,
								membership_status: selectedClient?.membership_status,
							},
						]}
						isLoading={false}
						showTopUpAction={typeof onTopUpClick === "function" && !hasOpenMembershipSale}
						onTopUpClick={
							typeof onTopUpClick === "function"
								? (flowType) => onTopUpClick(flowType, selectedClient)
								: undefined
						}
						hasOpenMembershipSale={hasOpenMembershipSale}
						onOpenMembershipSale={
							typeof onOpenMembershipSale === "function"
								? () => onOpenMembershipSale(selectedClient)
								: undefined
						}
						openMembershipSaleLabel={openMembershipSaleLabel}
					/>
					{/* {safeCharge > openingBalanceNum && (
						<div className={styles.messageContainer}>
							<p className={styles.errorMessage}>Insufficient cashless balance</p>
						</div>
					)} */}
				</div>
			) : (
				<div
					className={`dropdownToggle cursor-pointer ${styles.membershipToggle}`}
					onClick={() => {
						const next = !isDropdownOpen;
						setDropdownOpen(next);
						onDropdownToggle?.(next);
					}}
				>
					<span className={`${styles.toggleLabel} ${styles.togglePlaceholder}`}>
						{toggleLabel}
					</span>
					<DropDownIcon
						size={12}
						color="#6b7280"
						className={`${styles.dropdownIcon} ${
							isDropdownOpen ? styles.rotated : ""
						}`}
					/>
				</div>
			)}

			{isDropdownOpen && (
				<div className={styles.dropdownContainer}>
					<div className={styles.searchHeader}>
						<SearchBar
							ref={searchInputRef}
							placeholder="Search..."
							value={searchQuery}
							onChange={handleSearchChange}
							onKeyDown={handleKeyDown}
							className={styles.sidebarSearch}
							iconClassName={styles.rightbarSearch}
						/>
					</div>

					<ul
						className={styles.dropdownContainerList}
						ref={dropdownMenuRef}
						onScroll={handleListScroll}
					>
						{apiError && (
							<li className={`${styles.dropdownItem} ${styles.infoRow}`}>{apiError}</li>
						)}
						{!apiError && !isLoading && filteredCustomers.length === 0 && (
							<li className={`${styles.dropdownItem} no-item-found-placeholder`}>
								No results found
							</li>
						)}
						{!apiError &&
							filteredCustomers.map((customer, index) => {
								const isActive =
									selectedIndex === index ||
									Number(selectedClient?.user_id ?? selectedClient?.id) ===
										Number(customer?.id);
								const fullName = `${customer.name || ""} ${customer.lastname || ""}`.trim();
								const membershipExpired = isMembershipExpired(customer);
								return (
									<li
										key={`member-${customer.id || customer.name}-${index}`}
										data-key-nav-index={index}
										className={`hover ${styles.membershipItem} ${
											isActive ? styles.selectedItem : ""
										}`}
										title={membershipExpired ? "Membership expired" : undefined}
										onClick={() => handleSelect(customer)}
									>
										<div className={styles.membershipPrimary}>
											<span className={styles.membershipName}>
												{fullName || "—"}
											</span>
											<span className={styles.membershipNumber}>
												{" "}
												(#{customer.membership_number ?? "—"})
											</span>
											{membershipExpired ? (
												<span className={styles.expiredDot} aria-hidden />
											) : null}
										</div>
										<div className={styles.membershipPhone}>
											{customer.contact_number || "—"}
										</div>
									</li>
								);
							})}
						{isLoading && (
							<li className={`${styles.dropdownItem} no-item-found-placeholder`}>
								Loading...
							</li>
						)}
					</ul>
				</div>
			)}
		</div>
	);
};

export default MembershipClientDropdown;
